import { computed, inject, Injectable, signal } from '@angular/core';
import { UserApi } from '@core/api';
import { AppRoutesEnum, CurrentUser } from '@shared/types';
import * as momentTz from 'moment-timezone';

@Injectable({ providedIn: 'root' })
export class CurrentUserService {
    private userApi = inject(UserApi);

    private _currentUser = signal<CurrentUser>({
        userId: '',
        userName: '',
        firstName: '',
        lastName: '',
        fullName: '',
        isColonySuperAdmin: false,
        isColonyGroupAdmin: false,
        isColonyUserAdmin: false,
        isColonyMouseImportAdmin: false,
        isTLAdmin: false,
        isColonyWaiverAdmin: false,
    });
    private _isUnauthorized = signal(false);
    private _hasError = signal(false);

    readonly currentUser = computed(() => this._currentUser());
    readonly isUnauthorized = computed(() => this._isUnauthorized());
    readonly hasError = computed(() => this._hasError());

    readonly isTLAdmin = computed(() => this._currentUser().isTLAdmin);
    readonly isColonyGroupsAdmin = computed(
        () => this._currentUser().isColonySuperAdmin || this._currentUser().isColonyGroupAdmin
    );
    readonly isColonyWaiverAdmin = computed(() => this._currentUser().isColonyWaiverAdmin);

    async initializeCurrentUser(): Promise<CurrentUser | null> {
        if (this.currentUser().userId) {
            return this.currentUser();
        }

        const response = await this.userApi.getCurrentUserDetails();
        if (response.data) {
            this.setCurrentUser(response.data);
            momentTz.tz.setDefault('Etc/UTC');

            return this.currentUser();
        }

        this._hasError.set(true);
        return null;
    }

    setCurrentUser(user: CurrentUser): void {
        this._currentUser.set(user);
    }

    triggerUnauthorizedError(value: boolean): void {
        this._isUnauthorized.set(value);
    }

    getDefaultRoute(): string {
        if (this.isColonyGroupsAdmin() || this.isColonyWaiverAdmin()) {
            return AppRoutesEnum.GROUPS;
        }

        if (this.isTLAdmin()) {
            return AppRoutesEnum.TL_HUB;
        }

        return AppRoutesEnum.ERROR;
    }
}
